import { lazy, Suspense, type ReactNode } from 'react'
import { createBrowserRouter, Navigate, useParams } from 'react-router-dom'
import { AuthGuard } from './AuthGuard'
import { AppLayout } from './AppLayout'
import { RequireRole } from './RequireRole'
import { RouteErrorBoundary } from './RouteErrorBoundary'
import { NotFound } from './NotFound'
import { useAuthStore } from '@/stores/authStore'
import { LANDING_ROUTE, ROUTE_ROLES } from '@/config/roles'
import { FullPageLoading } from '@/design'

// A4 — every feature screen is lazily imported so the initial bundle stays small.
const LoginScreen = lazy(() => import('@/features/auth/LoginScreen'))
// Admin revamp, 31 Oct 2026: every role lands on Home.
const HomeScreen = lazy(() => import('@/features/home/HomeScreen'))
const SchoolsScreen = lazy(() => import('@/features/schools/SchoolsScreen'))
const SchoolDetailsScreen = lazy(() => import('@/features/schools/SchoolDetailsScreen'))
const UsersScreen = lazy(() => import('@/features/users/UsersScreen'))
const UserActivityScreen = lazy(() => import('@/features/users/UserActivityScreen'))
const SubscriptionStoryScreen = lazy(() => import('@/features/subscriptions/SubscriptionStoryScreen'))
const SubscriptionProblemsScreen = lazy(() => import('@/features/subscriptions/SubscriptionProblemsScreen'))
const ReceiptsScreen = lazy(() => import('@/features/receipts/ReceiptsScreen'))
const DashboardScreen = lazy(() => import('@/features/dashboard/DashboardScreen'))
const StudentsScreen = lazy(() => import('@/features/students/StudentsScreen'))
const TestsScreen = lazy(() => import('@/features/tests/TestsScreen'))
const CreateTestScreen = lazy(() => import('@/features/tests/CreateTestScreen'))
const GradesScreen = lazy(() => import('@/features/grades/GradesScreen'))
const SubjectsScreen = lazy(() => import('@/features/subjects/SubjectsScreen'))
const BooksScreen = lazy(() => import('@/features/books/BooksScreen'))
const VideosScreen = lazy(() => import('@/features/videos/VideosScreen'))
const LiveSessionsScreen = lazy(() => import('@/features/live-sessions/LiveSessionsScreen'))
const AnnouncementsScreen = lazy(() => import('@/features/announcements/AnnouncementsScreen'))
const TimetableScreen = lazy(() => import('@/features/timetable/TimetableScreen'))
const LeaderboardScreen = lazy(() => import('@/features/leaderboard/LeaderboardScreen'))
const ChallengeActivityScreen = lazy(() => import('@/features/challenge-activity/ChallengeActivityScreen'))
const ChallengeSettingsScreen = lazy(() => import('@/features/challenge-settings/ChallengeSettingsScreen'))
const PhoneCodesScreen = lazy(() => import('@/features/phone-codes/PhoneCodesScreen'))
// google-apple-1021
const SignInMethodsScreen = lazy(() => import('@/features/sign-in-methods/SignInMethodsScreen'))
const GuestSupportScreen = lazy(() => import('@/features/support/GuestSupportScreen'))
const FeatureAccessScreen = lazy(() => import('@/features/feature-access/FeatureAccessScreen')) // free/paid control (21 Oct 2026)
const RolloutScreen = lazy(() => import('@/features/rollout/RolloutScreen')) // Rollout switches (app 1.0.126)
// --- Novi Wardrobe (21 Oct 2026, wardrobe-items-1022) ---
const MascotWardrobeScreen = lazy(() => import('@/features/mascot-wardrobe/MascotWardrobeScreen'))
const FreePlanScreen = lazy(() => import('@/features/free-plan/FreePlanScreen')) // the free plan and the streak (30 Oct 2026)
const StreaksScreen = lazy(() => import('@/features/streaks/StreaksScreen')) // every child's streak (4 Nov 2026)
const RegistrationsScreen = lazy(() => import('@/features/registrations/RegistrationsScreen')) // registrations per day (1 Nov 2026)
const CurriculumCoverageScreen = lazy(() => import('@/features/curriculum-coverage/CurriculumCoverageScreen')) // curriculum coverage (2 Nov 2026)
const AutoPushesScreen = lazy(() => import('@/features/auto-pushes/AutoPushesScreen')) // automatic notifications per school (31 Oct 2026)
// --- end Novi Wardrobe ---
const TeacherQuestionsScreen = lazy(() => import('@/features/teacher-questions/TeacherQuestionsScreen'))
const QuestionBankScreen = lazy(() => import('@/features/question-bank/QuestionBankScreen'))
const TestPromptsScreen = lazy(() => import('@/features/test-prompts/TestPromptsScreen'))
const MonitoringScreen = lazy(() => import('@/features/monitoring/MonitoringScreen'))
const CostExplorerScreen = lazy(() => import('@/features/monitoring/CostExplorerScreen'))
// Running vs one-time from the real Google bill (1 Nov 2026)
const RunningCostScreen = lazy(() => import('@/features/monitoring/RunningCostScreen'))
const SupportScreen = lazy(() => import('@/features/support/SupportScreen'))
const TelegramScreen = lazy(() => import('@/features/telegram/TelegramScreen'))
const ActivityScreen = lazy(() => import('@/features/activity/ActivityScreen'))
const LegacyActivityRedirect = lazy(() => import('@/features/activity/ActivityScreen').then((m) => ({ default: m.LegacyRedirect })))
const ClassViewScreen = lazy(() => import('@/features/class-view/ClassViewScreen'))
const CacheScreen = lazy(() => import('@/features/cache/CacheScreen'))
const ProfileScreen = lazy(() => import('@/features/profile/ProfileScreen'))
// Child / detail screens (pushed routes)
// The one student page (admin revamp, 31 Oct 2026): /students/:id with tabs; it embeds the progress report.
const StudentPage = lazy(() => import('@/features/student-page/StudentPage'))
const AppVersionsScreen = lazy(() => import('@/features/app-versions/AppVersionsScreen'))
const EditTestScreen = lazy(() => import('@/features/tests/EditTestScreen'))
const VideoUploadScreen = lazy(() => import('@/features/videos/VideoUploadScreen'))
const LiveSessionFormScreen = lazy(() => import('@/features/live-sessions/LiveSessionFormScreen'))
// Paid live sessions (21 Oct 2026): the follow-up screen.
const LiveSessionFollowUpScreen = lazy(() => import('@/features/live-sessions/LiveSessionFollowUpScreen'))
const TeacherQuestionDetailScreen = lazy(() => import('@/features/teacher-questions/TeacherQuestionDetailScreen'))
const SupportConversationDetailScreen = lazy(() => import('@/features/support/SupportConversationDetailScreen'))
const KnowledgeBaseScreen = lazy(() => import('@/features/support/KnowledgeBaseScreen'))
const BookReviewScreen = lazy(() => import('@/features/books/BookReviewScreen'))

function Screen({ children }: { children: ReactNode }) {
  return <Suspense fallback={<FullPageLoading />}>{children}</Suspense>
}

/** Build a protected child route: role guard + per-route error boundary + lazy Suspense. */
function protectedRoute(path: string, element: ReactNode) {
  const roles = ROUTE_ROLES[path] ?? ['ADMIN', 'SCHOOL_ADMIN', 'PRINCIPAL', 'TEACHER']
  return {
    path,
    errorElement: <RouteErrorBoundary />,
    element: (
      <RequireRole roles={roles}>
        <Screen>{element}</Screen>
      </RequireRole>
    ),
  }
}

/** An old address of a student page: opens /students/:id on the given tab. */
function ToStudentPage({ param, tab }: { param: string; tab: string }) {
  const params = useParams()
  return <Navigate to={`/students/${params[param] ?? ''}?tab=${tab}`} replace />
}

function RoleLanding() {
  const user = useAuthStore((s) => s.user)
  return <Navigate to={user ? LANDING_ROUTE[user.role] : '/login'} replace />
}

function PublicOnly({ children }: { children: ReactNode }) {
  const status = useAuthStore((s) => s.status)
  const user = useAuthStore((s) => s.user)
  if (status === 'authenticated' && user) return <Navigate to={LANDING_ROUTE[user.role]} replace />
  return <>{children}</>
}

export const router = createBrowserRouter([
  {
    path: '/login',
    element: (
      <PublicOnly>
        <Screen>
          <LoginScreen />
        </Screen>
      </PublicOnly>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/',
    element: (
      <AuthGuard>
        <AppLayout />
      </AuthGuard>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <RoleLanding /> },
      protectedRoute('/home', <HomeScreen />),
      protectedRoute('/schools', <SchoolsScreen />),
      protectedRoute('/schools/:schoolId', <SchoolDetailsScreen />),
      protectedRoute('/users', <UsersScreen />),
      // A student's activity opens the one student page, Timeline tab; a staff account keeps the old screen.
      protectedRoute('/users/:userId/activity', <UserActivityScreen />),
      protectedRoute('/app-versions', <AppVersionsScreen />),
      protectedRoute('/users/:userId/subscription', <SubscriptionStoryScreen />),
      protectedRoute('/subscription-problems', <SubscriptionProblemsScreen />),
      protectedRoute('/receipts', <ReceiptsScreen />),
      protectedRoute('/telegram', <TelegramScreen />),
      protectedRoute('/activity', <ActivityScreen />),
      protectedRoute('/registrations', <RegistrationsScreen />), // registrations per day (1 Nov 2026)
      protectedRoute('/coverage', <CurriculumCoverageScreen />), // curriculum coverage, a tab of Exams (2 Nov 2026)
      // Replaced by /activity on 9 Oct 2026; the old addresses open the matching view there.
      protectedRoute('/usage', <LegacyActivityRedirect from="usage" />),
      protectedRoute('/student-activity', <LegacyActivityRedirect from="student-activity" />),
      protectedRoute('/class-view', <ClassViewScreen />),
      // Opens the one student page, Learning tab (31 Oct 2026).
      protectedRoute('/class-view/students/:studentId', <ToStudentPage param="studentId" tab="learning" />),
      protectedRoute('/dashboard', <DashboardScreen />),
      protectedRoute('/students', <StudentsScreen />),
      protectedRoute('/students/:studentId', <StudentPage />),
      protectedRoute('/tests', <TestsScreen />),
      protectedRoute('/tests/create', <CreateTestScreen />),
      protectedRoute('/tests/:testId/edit', <EditTestScreen />),
      protectedRoute('/grades', <GradesScreen />),
      protectedRoute('/subjects', <SubjectsScreen />),
      // Grades only, no classes (Omar, 31 Oct 2026): the old address opens Curriculum › Grades.
      protectedRoute('/classes', <Navigate to="/grades" replace />),
      protectedRoute('/books', <BooksScreen />),
      protectedRoute('/books/:bookId/review', <BookReviewScreen />),
      protectedRoute('/videos', <VideosScreen />),
      protectedRoute('/videos/upload', <VideoUploadScreen />),
      protectedRoute('/live-sessions', <LiveSessionsScreen />),
      protectedRoute('/live-sessions/new', <LiveSessionFormScreen />),
      protectedRoute('/live-sessions/:sessionId/edit', <LiveSessionFormScreen />),
      protectedRoute('/live-sessions/:sessionId/follow-up', <LiveSessionFollowUpScreen />),  // paid live sessions (21 Oct 2026)
      protectedRoute('/announcements', <AnnouncementsScreen />),
      protectedRoute('/timetable', <TimetableScreen />),
      protectedRoute('/leaderboard', <LeaderboardScreen />),
      protectedRoute('/challenge-activity', <ChallengeActivityScreen />),
      protectedRoute('/challenge-settings', <ChallengeSettingsScreen />),
      protectedRoute('/phone-codes', <PhoneCodesScreen />),
      protectedRoute('/feature-access', <FeatureAccessScreen />), // free/paid control (21 Oct 2026)
      protectedRoute('/rollout', <RolloutScreen />), // Rollout switches (app 1.0.126)
      protectedRoute('/mascot-wardrobe', <MascotWardrobeScreen />), // Novi Wardrobe (21 Oct 2026, wardrobe-items-1022)
      protectedRoute('/free-plan', <FreePlanScreen />), // the free plan and the streak (30 Oct 2026)
      protectedRoute('/streaks', <StreaksScreen />), // who started a streak, every child's days (4 Nov 2026)
      protectedRoute('/auto-pushes', <AutoPushesScreen />), // automatic notifications per school (31 Oct 2026)
      protectedRoute('/teacher-questions', <TeacherQuestionsScreen />),
      protectedRoute('/teacher-questions/:questionId', <TeacherQuestionDetailScreen />),
      protectedRoute('/question-bank', <QuestionBankScreen />),
      protectedRoute('/test-prompts', <TestPromptsScreen />),
      protectedRoute('/monitoring', <MonitoringScreen />),
      protectedRoute('/monitoring/cost-explorer', <CostExplorerScreen />),
      protectedRoute('/monitoring/running', <RunningCostScreen />), // running vs one-time, real bill (1 Nov 2026)
      protectedRoute('/cache', <CacheScreen />),
      protectedRoute('/support', <SupportScreen />),
      protectedRoute('/support/knowledge', <KnowledgeBaseScreen />),
      protectedRoute('/support/guests', <GuestSupportScreen />), // google-apple-1021
      protectedRoute('/sign-in-methods', <SignInMethodsScreen />), // google-apple-1021
      protectedRoute('/support/:conversationId', <SupportConversationDetailScreen />),
      protectedRoute('/profile', <ProfileScreen />),
      { path: '*', element: <NotFound /> },
    ],
  },
])
